import type { CompleteUploadInput, RequestUploadUrlOutput } from '@terafile/shared'
import { abortFileUpload, completeUpload, requestUploadUrl } from './files-api.js'
import {
  MAX_PUT_ATTEMPTS,
  PutAttemptError,
  isRetryableStatus,
  pickParallelism,
  withRetry,
} from './upload-retry.js'

/**
 * R2 直アップロードのクライアント実装。
 *
 * - 単発: 1回の PUT で完了
 * - マルチパート: 各 part を並列 PUT し、ETag を集めて complete 通知
 *
 * 動画変換は complete 後にサーバ側で R2 → Cloudflare Stream に Copy する方式。
 * クライアントは R2 にのみ送信し、Stream 取り込みはサーバ任せ（200MB 制限・上り 2 倍を回避）。
 *
 * UI で進捗バーを描けるよう XHR ベースで実装。fetch では Upload Progress が取れない。
 */

type UploadProgress = {
  loadedBytes: number
  totalBytes: number
}

type UploadOptions = {
  signal?: AbortSignal
  onProgress?: (progress: UploadProgress) => void
}

/**
 * 全アップロード横断の同時 PUT 上限。
 *
 * ファイル単位の並列度（pickParallelism）とは別に、ブラウザ全体で同時に飛ぶ
 * R2 への PUT 本数をこの値で頭打ちにする。複数ファイルを一度に投入しても
 * （例: 4 ファイル × 各 8 並列 = 32 本）ここで 8 本に絞られるため、
 * 接続断 / net::ERR_INSUFFICIENT_RESOURCES（XHR error イベント）を防ぐ。
 *
 * 単一ファイルなら pickParallelism(=最大 8) と同値なので挙動は変わらない。
 */
const MAX_GLOBAL_CONCURRENT_PUTS = 8

/**
 * 公平（FIFO）なカウンティング・セマフォ。
 * release 時に待機者がいればスロットを「明け渡す」ことで over-subscription を防ぐ。
 */
class Semaphore {
  private active = 0
  private readonly waiters: (() => void)[] = []
  constructor(private readonly max: number) {}

  async acquire(): Promise<void> {
    if (this.active < this.max) {
      this.active += 1
      return
    }
    // 空きが無ければ待機列に並ぶ。release からスロットを引き継ぐので active は増やさない。
    await new Promise<void>((resolve) => this.waiters.push(resolve))
  }

  release(): void {
    const next = this.waiters.shift()
    if (next) {
      // スロットを待機者へ明け渡す（active は据え置き）
      next()
    } else {
      this.active -= 1
    }
  }
}

const uploadSlots = new Semaphore(MAX_GLOBAL_CONCURRENT_PUTS)

export type UploadResult = {
  fileId: string
}

export async function uploadFile(
  projectId: string,
  file: File,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  const issued = await requestUploadUrl(projectId, {
    originalName: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
  })

  const uploadIdForCleanup = issued.upload.kind === 'multipart' ? issued.upload.uploadId : undefined

  try {
    // R2 への直接アップロード（Architecture §6.1）。
    // 動画ファイルでも Stream にはサーバが complete 後に R2 → Copy する。
    const parts =
      issued.upload.kind === 'single'
        ? await putSingle(file, issued.upload.uploadUrl, opts).then(() => null)
        : await uploadMultipart(file, issued.upload, opts)

    const completeInput: CompleteUploadInput =
      issued.upload.kind === 'single'
        ? { upload: { kind: 'single' } }
        : {
            upload: {
              kind: 'multipart',
              uploadId: issued.upload.uploadId,
              parts: parts as { partNumber: number; etag: string }[],
            },
          }
    await completeUpload(projectId, issued.fileId, completeInput)
    return { fileId: issued.fileId }
  } catch (err) {
    // 中断（AbortSignal 経由）された場合は、サーバ側に掃除を依頼する。
    // - DB の暫定 File レコードを削除
    // - multipart は R2 の in-flight upload も中断
    // best-effort: 掃除 API 自体が失敗してもユーザにはアップロードのキャンセル結果を返す。
    if (opts.signal?.aborted) {
      try {
        await abortFileUpload(projectId, issued.fileId, uploadIdForCleanup)
      } catch {
        // 失敗しても R2 Lifecycle Rule や運用バッチで最終的に掃除される
      }
    }
    throw err
  }
}

function putSingleOnce(file: File, url: string, opts: UploadOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        opts.onProgress?.({ loadedBytes: e.loaded, totalBytes: e.total })
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else
        reject(
          new PutAttemptError(
            `R2 PUT failed: HTTP ${xhr.status} — ${truncate(xhr.responseText)}`,
            isRetryableStatus(xhr.status),
            xhr.status,
          ),
        )
    })
    xhr.addEventListener('error', () =>
      reject(new PutAttemptError('R2 PUT network error (CORS / 接続切断 / DNS の可能性)', true)),
    )
    xhr.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')))
    if (opts.signal) {
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }
    xhr.send(file)
  })
}

async function putSingle(file: File, url: string, opts: UploadOptions): Promise<void> {
  // 瞬断（モバイル回線で頻発）は1回で諦めず指数バックオフで再送する。
  await withRetry(
    async () => {
      await uploadSlots.acquire()
      try {
        return await putSingleOnce(file, url, opts)
      } finally {
        uploadSlots.release()
      }
    },
    { attempts: MAX_PUT_ATTEMPTS, signal: opts.signal },
  )
}

type Multipart = Extract<RequestUploadUrlOutput['upload'], { kind: 'multipart' }>

async function uploadMultipart(
  file: File,
  multi: Multipart,
  opts: UploadOptions,
): Promise<{ partNumber: number; etag: string }[]> {
  const partSize = multi.partSizeBytes
  const totalBytes = file.size
  const loadedPerPart = new Map<number, number>()
  const reportProgress = () => {
    let loaded = 0
    for (const v of loadedPerPart.values()) loaded += v
    opts.onProgress?.({ loadedBytes: loaded, totalBytes })
  }

  const queue = [...multi.parts]
  const results: { partNumber: number; etag: string }[] = []

  async function worker() {
    while (queue.length > 0) {
      const part = queue.shift()
      if (!part) return
      const start = (part.partNumber - 1) * partSize
      const end = Math.min(start + partSize, file.size)
      const blob = file.slice(start, end)
      const partOpts: UploadOptions = {
        onProgress: ({ loadedBytes }) => {
          loadedPerPart.set(part.partNumber, loadedBytes)
          reportProgress()
        },
      }
      if (opts.signal) partOpts.signal = opts.signal
      const etag = await putPart(blob, part.uploadUrl, partOpts)
      results.push({ partNumber: part.partNumber, etag })
    }
  }

  // ファイルサイズに応じて並列度を変える（小ファイルは少なく、大ファイルは多く）
  const parallelism = pickParallelism(totalBytes)
  const workers = Array.from({ length: Math.min(parallelism, multi.parts.length) }, worker)
  await Promise.all(workers)
  return results.sort((a, b) => a.partNumber - b.partNumber)
}

function putPartOnce(blob: Blob, url: string, opts: UploadOptions): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        opts.onProgress?.({ loadedBytes: e.loaded, totalBytes: e.total })
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // R2 は ETag をレスポンスに含めるが CORS で expose されないと取れない。
        // Cloudflare R2 のデフォルトでは ETag は expose されるが、独自バケット設定で
        // expose-headers から外していると ETag が null になり multipart 完了不可になる。
        const etag = xhr.getResponseHeader('ETag')
        if (!etag) {
          // CORS 設定起因で再送しても直らないため retryable=false。
          reject(
            new PutAttemptError(
              'R2 part upload returned no ETag (CORS の expose-headers に "ETag" を含めてください)',
              false,
            ),
          )
        } else {
          resolve(etag)
        }
      } else {
        reject(
          new PutAttemptError(
            `R2 part PUT failed: HTTP ${xhr.status} — ${truncate(xhr.responseText)}`,
            isRetryableStatus(xhr.status),
            xhr.status,
          ),
        )
      }
    })
    xhr.addEventListener('error', () =>
      reject(
        new PutAttemptError('R2 part network error (CORS / 接続切断 / 署名期限切れの可能性)', true),
      ),
    )
    xhr.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')))
    if (opts.signal) {
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }
    xhr.send(blob)
  })
}

async function putPart(blob: Blob, url: string, opts: UploadOptions): Promise<string> {
  // 瞬断（net::ERR_CONNECTION_RESET 等）は1回で諦めず指数バックオフで再送する。
  // 署名 URL は 1h 有効なので、数秒のリトライ中は同じ URL を再利用できる。
  return withRetry(
    async () => {
      await uploadSlots.acquire()
      try {
        return await putPartOnce(blob, url, opts)
      } finally {
        uploadSlots.release()
      }
    },
    { attempts: MAX_PUT_ATTEMPTS, signal: opts.signal },
  )
}

function truncate(s: string | null | undefined): string {
  if (!s) return ''
  const max = 300
  return s.length > max ? `${s.slice(0, max)}…` : s
}
